import {
    BIOMETRY_KINDS,
    BRIDGE_HOSTS,
    BRIDGE_LIMITS,
    BRIDGE_METHODS,
    BRIDGE_PLATFORMS,
    HAPTIC_STYLES,
    NOTIFICATION_STATES,
} from '../constants/bridge';

// Keys that must never travel through the bridge in either direction: the
// draft keeps local device checks fully separate from the server session.
const FORBIDDEN_KEYS = new Set([
    'token', 'auth', 'authorization', 'session', 'cookie', 'password',
    'credential', 'credentials', 'secret', 'jwt', 'csrf', 'script', 'eval', 'command',
]);

const invalid = (reason) => ({ valid: false, reason });
const valid = (value) => ({ valid: true, value });

export function isPlainObject(candidate) {
    if (candidate === null || typeof candidate !== 'object') return false;
    const proto = Object.getPrototypeOf(candidate);
    return proto === Object.prototype || proto === null;
}

function stringRule({ min = 0, max, optional = false }) {
    return (input) => {
        if (input === undefined) return optional ? valid(undefined) : invalid('requerido');
        if (typeof input !== 'string') return invalid('debe ser texto');
        const trimmed = input.trim();
        if (trimmed.length < min) return invalid('demasiado corto');
        if (trimmed.length > max) return invalid('demasiado largo');
        return valid(trimmed);
    };
}

function enumRule(values) {
    return (input) => (values.includes(input) ? valid(input) : invalid('valor no permitido'));
}

function booleanRule() {
    return (input) => (typeof input === 'boolean' ? valid(input) : invalid('debe ser booleano'));
}

function literalTrueRule() {
    return (input) => (input === true ? valid(true) : invalid('debe ser true'));
}

function httpUrlRule() {
    return (input) => {
        if (input === undefined) return valid(undefined);
        if (typeof input !== 'string' || input.length > BRIDGE_LIMITS.URL_MAX) return invalid('URL no válida');
        try {
            const parsed = new URL(input);
            if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return invalid('esquema no permitido');
            if (parsed.username || parsed.password) return invalid('URL con credenciales');
            return valid(parsed.toString());
        } catch {
            return invalid('URL no válida');
        }
    };
}

function objectRule(shape, { refine } = {}) {
    return (input) => {
        const source = input === undefined ? {} : input;
        if (!isPlainObject(source)) return invalid('debe ser un objeto');
        for (const key of Object.keys(source)) {
            if (FORBIDDEN_KEYS.has(key.toLowerCase())) return invalid(`campo prohibido: ${key}`);
            if (!Object.hasOwn(shape, key)) return invalid(`campo no permitido: ${key}`);
        }
        const out = {};
        for (const [key, rule] of Object.entries(shape)) {
            const checked = rule(source[key]);
            if (!checked.valid) return invalid(`${key}: ${checked.reason}`);
            if (checked.value !== undefined) out[key] = checked.value;
        }
        const reason = refine?.(out);
        return reason ? invalid(reason) : valid(out);
    };
}

const EMPTY = objectRule({});

const ARGS = {
    [BRIDGE_METHODS.CAPABILITIES]: EMPTY,
    [BRIDGE_METHODS.BIOMETRIC_AUTHENTICATE]: objectRule({
        reason: stringRule({ min: 1, max: BRIDGE_LIMITS.BIOMETRIC_REASON_MAX }),
    }),
    [BRIDGE_METHODS.NOTIFICATION_PERMISSION]: EMPTY,
    [BRIDGE_METHODS.NOTIFICATION_REQUEST]: EMPTY,
    [BRIDGE_METHODS.NOTIFICATION_LOCAL]: objectRule({
        title: stringRule({ min: 1, max: BRIDGE_LIMITS.NOTIFICATION_TITLE_MAX }),
        body: stringRule({ max: BRIDGE_LIMITS.NOTIFICATION_BODY_MAX, optional: true }),
    }),
    [BRIDGE_METHODS.SHARE]: objectRule({
        title: stringRule({ max: BRIDGE_LIMITS.SHARE_TITLE_MAX, optional: true }),
        text: stringRule({ max: BRIDGE_LIMITS.SHARE_TEXT_MAX, optional: true }),
        url: httpUrlRule(),
    }, {
        refine: (value) => (value.text || value.url ? null : 'text o url requerido'),
    }),
    [BRIDGE_METHODS.HAPTIC]: objectRule({ style: enumRule(HAPTIC_STYLES) }),
};

const PERMISSION_VALUE = objectRule({ state: enumRule(NOTIFICATION_STATES) });

const VALUES = {
    [BRIDGE_METHODS.CAPABILITIES]: objectRule({
        platform: enumRule(BRIDGE_PLATFORMS),
        host: enumRule(BRIDGE_HOSTS),
        share: booleanRule(),
        haptics: booleanRule(),
        localNotifications: booleanRule(),
        biometric: objectRule({
            available: booleanRule(),
            kind: enumRule(BIOMETRY_KINDS),
            reason: stringRule({ max: BRIDGE_LIMITS.MESSAGE_MAX, optional: true }),
        }),
    }),
    // Only a boolean and a modality ever come back: no biometric data,
    // templates or identifiers leave the device.
    [BRIDGE_METHODS.BIOMETRIC_AUTHENTICATE]: objectRule({
        verified: literalTrueRule(),
        kind: enumRule(BIOMETRY_KINDS),
    }),
    [BRIDGE_METHODS.NOTIFICATION_PERMISSION]: PERMISSION_VALUE,
    [BRIDGE_METHODS.NOTIFICATION_REQUEST]: PERMISSION_VALUE,
    [BRIDGE_METHODS.NOTIFICATION_LOCAL]: objectRule({ scheduled: literalTrueRule() }),
    [BRIDGE_METHODS.SHARE]: objectRule({ shared: literalTrueRule() }),
    [BRIDGE_METHODS.HAPTIC]: objectRule({ done: literalTrueRule() }),
};

export const ALLOWED_METHODS = Object.freeze(Object.keys(ARGS));

export function isAllowedMethod(method) {
    return typeof method === 'string' && Object.hasOwn(ARGS, method);
}

export function validateArgs(method, payload) {
    if (!isAllowedMethod(method)) return invalid('método no permitido');
    return ARGS[method](payload);
}

export function validateValue(method, value) {
    if (!isAllowedMethod(method)) return invalid('método no permitido');
    return VALUES[method](value);
}
